import {
  Component, OnInit, OnChanges, Input, Output, SimpleChanges, EventEmitter
} from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { TargetPlatform } from 'src/app/models/services/audiences/target-platform';
import { TargetPlatforms } from 'src/app/models/services/targetPlatforms.enum';
import { TargetPlatformsTypes } from 'src/app/models/services/targetPlatformsType.enum';

type Grouped = { group: string; items: TargetPlatform[] };

@Component({
  selector: 'eris-platform-picker',
  templateUrl: './eris-platform-picker.component.html',
  styleUrls: ['./eris-platform-picker.component.sass']
})
export class ErisPlatformPickerComponent implements OnInit, OnChanges {

  @Input() platforms: TargetPlatform[] = [];
  @Input() buttonsPerRow: number = 2;
  @Input() selectedId: number | null = null;
  @Input() groupOrder?: Array<TargetPlatformsTypes | string>;
  @Input() itemOrderByGroup?: {
    [groupKey: string]: Array<number | string>;
  };
  @Input() itemDefaultOrder: 'name' | 'id' = 'name';

  @Output() selectedChange = new EventEmitter<TargetPlatform>();

  groups: Grouped[] = [];


  ngOnInit(): void {
    this.rebuild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['platforms']) {
      this.rebuild();
    } else if (changes['selectedId'] && !changes['selectedId'].firstChange) {
      this.ensureSelection();
    }
  }

  private rebuild(): void {
    const src = (this.platforms || []).filter(p => p.enabled !== false);

    if (!src.length) {
      this.groups = [];
      this.selectedId = null;
      return;
    }

    const map = new Map<TargetPlatformsTypes, TargetPlatform[]>();

    for (const p of src) {
      const raw = (p as any).platformType;
      const type = this.normalizeToEnum(raw);
      if (!map.has(type)) map.set(type, []);
      map.get(type)!.push(p);
    }

    for (const [type, arr] of Array.from(map.entries())) {
      this.sortItemsInGroup(arr, type);
    }

    this.groups = Array.from(map.entries())
      .sort((a, b) => this.groupWeightByType(a[0]) - this.groupWeightByType(b[0]))
      .map(([type, items]) => ({ group: this.typeToLabel(type), items }));

    this.ensureSelection();
  }

  onGroupChange(e: MatButtonToggleChange): void {
    this.selectedId = e.value as number;
    const chosen = this.findById(this.selectedId as number);
    if (chosen) this.selectedChange.emit(chosen);
  }

  private findById(id?: number): TargetPlatform | undefined {
    if (id == null) return undefined;
    for (const g of this.groups) {
      const hit = g.items.find(it => it.id === id);
      if (hit) return hit;
    }
    return undefined;
  }

  getTransferFileIcon(tp: TargetPlatform): string {
    const id = tp.id as number;
    return `assets/images/target-platforms/${id}.png`;
  }

  trackById(_: number, p: TargetPlatform) {
    return p.id ?? p.name;
  }

  private groupWeightByType(type: TargetPlatformsTypes): number {
    if (this.groupOrder && this.groupOrder.length) {
      for (let i = 0; i < this.groupOrder.length; i++) {
        const entry = this.groupOrder[i];
        const entryType = typeof entry === 'number'
          ? entry as TargetPlatformsTypes
          : this.normalizeToEnum(entry);
        if (entryType === type) return i;
      }
      return Number.MAX_SAFE_INTEGER;
    }
    return type;
  }

  private normalizeToEnum(val: unknown): TargetPlatformsTypes {
    const E: any = TargetPlatformsTypes;

    if (typeof val === 'number' && E[val] !== undefined) {
      return val as TargetPlatformsTypes;
    }
    if (typeof val === 'string') {
      const s = val.trim();

      const key = Object.keys(E).filter(k => isNaN(Number(k)))
        .find(k => k.toLowerCase() === s.toLowerCase());
      if (key) return E[key] as TargetPlatformsTypes;

      const n = Number(s);
      if (!Number.isNaN(n) && E[n] !== undefined) {
        return n as TargetPlatformsTypes;
      }
    }
    return E.Other as TargetPlatformsTypes;
  }

  private typeToLabel(type: TargetPlatformsTypes): string {
    return (TargetPlatformsTypes as any)[type] ?? 'Other';
  }

  private ensureSelection(): void {
    const all: TargetPlatform[] = ([] as TargetPlatform[])
      .concat(...this.groups.map(g => g.items))
      .filter(p => p.enabled !== false);

    if (!all.length) {
      this.selectedId = null;
      return;
    }
    const keep = this.selectedId != null && all.some(p => p.id === this.selectedId);
    if (!keep) {
      const first = all[0];
      this.selectedId = first.id as number;
      this.selectedChange.emit(first);
    }
  }

  private sortItemsInGroup(items: TargetPlatform[], type: TargetPlatformsTypes): void {
    const label = this.typeToLabel(type);
    const keyNum = String(type);
    const cfg =
      this.itemOrderByGroup?.[keyNum] ??
      this.itemOrderByGroup?.[label];

    if (cfg && cfg.length) {
      const rank = this.buildRankMap(cfg, items);
      items.sort((a, b) => {
        const ra = rank.get(a.id as number);
        const rb = rank.get(b.id as number);
        if (ra != null && rb != null) return ra - rb;
        if (ra != null) return -1;
        if (rb != null) return 1;
        return this.itemDefaultOrder === 'id'
          ? (a.id as number) - (b.id as number)
          : (a.name || '').localeCompare(b.name || '');
      });
      return;
    }

    if (this.itemDefaultOrder === 'id') {
      items.sort((a, b) => (a.id as number) - (b.id as number));
    } else {
      items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
  }

  private buildRankMap(order: Array<number | string>, items: TargetPlatform[]): Map<number, number> {
    const E: any = TargetPlatforms;
    const byName = new Map<string, number>(
      items.map(it => [String(it.name).trim().toLowerCase(), it.id as number])
    );

    const rank = new Map<number, number>();
    order.forEach((entry, idx) => {
      let id: number | undefined;

      if (typeof entry === 'number') {
        id = entry;
      } else {
        const s = String(entry).trim();

        if (E[s] !== undefined && typeof E[s] === 'number') {
          id = E[s] as number;
        } else {
          const byVisibleName = byName.get(s.toLowerCase());
          if (byVisibleName != null) id = byVisibleName;
          else {
            const n = Number(s);
            if (!Number.isNaN(n)) id = n;
          }
        }
      }

      if (id != null && !rank.has(id)) rank.set(id, idx);
    });
    return rank;
  }
}
